import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Error handling utilities
const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Server responded with an error
    console.error('Server response error:', {
      status: error.response.status,
      data: error.response.data
    });
    const message = error.response.data?.message || 'An error occurred';
    if (error.response.status === 401) {
      // Clear both admin and partner tokens on unauthorized
      localStorage.removeItem('adminToken');
      localStorage.removeItem('Authentication:');
      localStorage.removeItem('partnerToken');
    }
    throw new Error(message);
  } else if (error.request) {
    // Request was made but no response received
    console.error('No response received:', error.request);
    throw new Error('Unable to connect to the server. Please check your internet connection.');
  } else {
    // Something happened in setting up the request
    console.error('Request setup error:', error.message);
    throw new Error(error.message || 'An error occurred while processing your request.');
  }
};

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken') || 
                  localStorage.getItem('Authentication:') || 
                  localStorage.getItem('partnerToken') ||
                  localStorage.getItem('driverToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(handleApiError(error))
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => Promise.reject(handleApiError(error))
);

// Auth endpoints
export const auth = {
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw handleApiError(error);
    }
  },
  logout: async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('Authentication:');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  register: async (data) => {
    try {
      return await api.post('/auth/register', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getCurrentUser: async () => {
    try {
      return await api.get('/auth/me');
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Car endpoints
export const cars = {
  getAll: async () => {
    try {
      return await api.get('/cars');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAvailable: async (params) => {
    try {
      return await api.get('/cars/available', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/cars/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (formData) => {
    try {
      // Update headers for FormData
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      return await api.post('/cars', formData, config);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/cars/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateAvailability: async (id, data) => {
    try {
      return await api.patch(`/cars/${id}/availability`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/cars/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Rental endpoints
export const rentals = {
  create: async (data) => {
    try {
      return await api.post('/rentals', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAll: async () => {
    try {
      return await api.get('/rentals');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/rentals/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getByPartner: async (partnerId) => {
    try {
      return await api.get(`/rentals/partner/${partnerId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateStatus: async (id, status) => {
    try {
      return await api.patch(`/rentals/${id}/status`, { status });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updatePaymentStatus: async (id, isPaid) => {
    try {
      return await api.patch(`/rentals/${id}/payment`, { isPaid });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateCommissionPayment: async (id, commissionPaid) => {
    try {
      return await api.patch(`/rentals/${id}/commission-payment`, { commissionPaid });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getDurationDiscounts: async () => {
    try {
      return await api.get('/rentals/duration-discounts');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateDurationDiscount: async (term, discount) => {
    try {
      return await api.patch('/rentals/duration-discounts', { term, discount });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  delete: async (id) => {
    try {
      return await api.delete(`/rentals/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Coupon endpoints
export const coupons = {
  validate: async (code, orderAmount) => {
    try {
      return await api.get(`/coupons/validate/${code}`, { params: { orderAmount } });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAll: async () => {
    try {
      return await api.get('/coupons');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/coupons', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/coupons/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/coupons/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deactivate: async (id) => {
    try {
      return await api.patch(`/coupons/${id}/deactivate`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Finance endpoints
export const finances = {
  getTransactions: async (params) => {
    try {
      return await api.get('/finances/transactions', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createTransaction: async (data) => {
    try {
      return await api.post('/finances/transactions', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateTransaction: async (id, data) => {
    try {
      return await api.put(`/finances/transactions/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteTransaction: async (id) => {
    try {
      return await api.delete(`/finances/transactions/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getBanks: async () => {
    try {
      return await api.get('/finances/banks');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createBank: async (data) => {
    try {
      return await api.post('/finances/banks', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateBank: async (id, data) => {
    try {
      return await api.put(`/finances/banks/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteBank: async (id) => {
    try {
      return await api.delete(`/finances/banks/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getFunds: async () => {
    try {
      return await api.get('/finances/funds');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createFund: async (data) => {
    try {
      return await api.post('/finances/funds', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateFund: async (id, data) => {
    try {
      return await api.put(`/finances/funds/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteFund: async (id) => {
    try {
      return await api.delete(`/finances/funds/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getSummary: async (params) => {
    try {
      return await api.get('/finances/summary', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Partner endpoints
export const partners = {
  getAll: async () => {
    try {
      return await api.get('/partners');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/partners/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/partners', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/partners/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/partners/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getRentals: async (partnerId) => {
    try {
      return await api.get(`/partners/${partnerId}/rentals`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Contact endpoints
export const contact = {
  submitContactForm: async (data) => {
    try {
      return await api.post('/contact/contact', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  submitTransferRequest: async (data) => {
    try {
      return await api.post('/contact/transfer', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAllTransfers: async () => {
    try {
      return await api.get('/contact/transfers');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getTransferById: async (id) => {
    try {
      return await api.get(`/contact/transfers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateTransferStatus: async (id, status) => {
    try {
      return await api.patch(`/contact/transfers/${id}/status`, { status });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteTransfer: async (id) => {
    try {
      return await api.delete(`/contact/transfers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getTransferPriceEstimate: async (pickup, dropoff, date, serviceType, returnLocation, carType) => {
    try {
      return await api.get(`/contact/transfer-price?pickup=${pickup}&dropoff=${dropoff}&date=${date}&serviceType=${serviceType}&returnLocation=${returnLocation}&carType=${carType || 'car'}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getPartnerTransfers: async (partnerId) => {
    try {
      return await api.get(`/contact/partner-transfers/${partnerId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Driver endpoints
export const drivers = {
  getAllDrivers: async () => {
    try {
      return await api.get('/drivers');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getDriverById: async (id) => {
    try {
      return await api.get(`/drivers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createDriver: async (data) => {
    try {
      return await api.post('/drivers', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateDriver: async (id, data) => {
    try {
      return await api.put(`/drivers/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteDriver: async (id) => {
    try {
      return await api.delete(`/drivers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  assignDriver: async (transferId, driverId, driverNotes) => {
    try {
      return await api.post(`/drivers/assign/${transferId}`, { 
        driverId, 
        driverNotes 
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  unassignDriver: async (transferId) => {
    try {
      return await api.post(`/drivers/unassign/${transferId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateDriverPassword: async (id, password) => {
    try {
      return await api.patch(`/drivers/${id}/password`, { password });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  login: async (credentials) => {
    try {
      return await api.post('/drivers/login', credentials);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getProfile: async () => {
    try {
      return await api.get('/drivers/profile/me');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAssignedTransfers: async () => {
    try {
      return await api.get('/drivers/transfers/assigned');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  completeTransfer: async (transferId) => {
    try {
      return await api.patch(`/drivers/transfers/${transferId}/complete`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addTransferNotes: async (transferId, notes) => {
    try {
      return await api.patch(`/drivers/transfers/${transferId}/notes`, { notes });
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export const transferPricing = {
  getAll: async () => {
    try {
      return await api.get('/transfer-pricing');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/transfer-pricing/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/transfer-pricing', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/transfer-pricing/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/transfer-pricing/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  bulkUpdate: async (ids, updates) => {
    try {
      return await api.patch('/transfer-pricing/bulk-update', { ids, updates });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  bulkImport: async (data) => {
    try {
      return await api.post('/transfer-pricing/bulk', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  list: async (params) => {
    try {
      return await api.get('/transfer-pricing/list', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Tours endpoints
export const tours = {
  getAll: async () => {
    try {
      return await api.get('/tours');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAllAdmin: async () => {
    try {
      return await api.get('/tours/admin');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/tours/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/tours', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/tours/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/tours/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  toggleActive: async (id) => {
    try {
      return await api.patch(`/tours/${id}/toggle-active`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export const tourBookings = {
  getAll: async () => {
    try {
      return await api.get('/tour-bookings');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getByTransfer: async (transferId) => {
    try {
      return await api.get(`/tour-bookings/transfer/${transferId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/tour-bookings', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateStatus: async (id, status) => {
    try {
      return await api.patch(`/tour-bookings/${id}/status`, { status });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/tour-bookings/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getPriceEstimate: async (tourId, date, persons) => {
    try {
      return await api.get(`/tour-bookings/price-estimate?tourId=${tourId}&date=${date}&persons=${persons}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export default {
  auth,
  cars,
  rentals,
  coupons,
  finances,
  partners,
  contact,
  drivers,
  transferPricing,
  tours,
  tourBookings
};